import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SkeletonTable } from '../components/Skeleton';
import { api } from '../utils/api-client';

interface AffiliateLink {
  id: string;
  name: string;
  shortCode: string;
  destinationUrl: string;
  totalClicks: number;
  totalConversions: number;
  totalRevenue: number;
  createdAt: string;
}

interface LinksResponse {
  success: boolean;
  data?: {
    links?: AffiliateLink[];
  };
}

export function LinksPage(): ReactNode {
  const [selectedLinks, setSelectedLinks] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['links'],
    queryFn: async (): Promise<LinksResponse> => {
      return await api.get<LinksResponse>('/affiliate/links');
    },
  });

  const links: AffiliateLink[] = data?.data?.links ?? [];

  const filteredLinks = links.filter(link => {
    if (filterStatus === 'active' && link.totalClicks === 0) return false;
    if (filterStatus === 'inactive' && link.totalClicks > 0) return false;
    if (searchQuery && !link.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !link.shortCode.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const copyLink = (link: AffiliateLink): void => {
    const url = `https://r.profitos.pro/${link.shortCode}`;
    void navigator.clipboard.writeText(url);
  };

  const handleCopyLink = copyLink;

  const handleBulkCopy = (): void => {
    const urls = Array.from(selectedLinks)
      .map(id => {
        const link = links.find(l => l.id === id);
        return link ? `https://r.profitos.pro/${link.shortCode}` : null;
      })
      .filter((url): url is string => url !== null)
      .join('\n');
    
    void navigator.clipboard.writeText(urls);
    setSelectedLinks(new Set());
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Affiliate Links</h2>
          <p className="text-gray-500">
            Track and manage your affiliate links
          </p>
        </div>
        <div className="flex gap-2">
          {selectedLinks.size > 0 && (
            <button
              onClick={handleBulkCopy}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Copy {selectedLinks.size} Selected
            </button>
          )}
          <button className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
            + Create Link
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search links..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); }}
          className="flex-1 rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value as typeof filterStatus); }}
          className="rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="all">All Links</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Links</h3>
          <p className="mt-2 text-3xl font-bold">{links.length}</p>
        </div>
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Clicks</h3>
          <p className="mt-2 text-3xl font-bold">
            {links.reduce((sum, l) => sum + l.totalClicks, 0).toLocaleString()}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-sm font-medium text-gray-500">Conversions</h3>
          <p className="mt-2 text-3xl font-bold">
            {links.reduce((sum, l) => sum + l.totalConversions, 0).toLocaleString()}
          </p>
        </div>
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-sm font-medium text-gray-500">Revenue</h3>
          <p className="mt-2 text-3xl font-bold">
            ${links.reduce((sum, l) => sum + l.totalRevenue, 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Links Table */}
      {isLoading ? (
        <SkeletonTable rows={5} columns={5} />
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-600">Failed to load links. Please try again.</p>
        </div>
      ) : filteredLinks.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {searchQuery || filterStatus !== 'all' ? 'No links match your filters' : 'No links yet'}
          </h3>
          <p className="mt-2 text-gray-500">
            {searchQuery || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters.'
              : 'Create your first affiliate link to start tracking.'}
          </p>
          {(!searchQuery && filterStatus === 'all') && (
            <button className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
              Create Link
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedLinks.size === filteredLinks.length && filteredLinks.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLinks(new Set(filteredLinks.map(l => l.id)));
                      } else {
                        setSelectedLinks(new Set());
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clicks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CTR
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLinks.map((link) => (
                <tr key={link.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedLinks.has(link.id)}
                      onChange={(e) => {
                        const newSet = new Set(selectedLinks);
                        if (e.target.checked) {
                          newSet.add(link.id);
                        } else {
                          newSet.delete(link.id);
                        }
                        setSelectedLinks(newSet);
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{link.name}</div>
                    <div className="text-sm text-primary-600 flex items-center gap-2">
                      <span>r.profitos.pro/{link.shortCode}</span>
                      <button
                        onClick={() => handleCopyLink(link)}
                        className="text-gray-400 hover:text-primary-600"
                        title="Copy link"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {link.totalClicks.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {link.totalConversions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${link.totalRevenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {link.totalClicks > 0
                      ? ((link.totalConversions / link.totalClicks) * 100).toFixed(1)
                      : 0}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(link.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <a
                      href={`/analytics?link=${link.id}`}
                      className="text-primary-600 hover:text-primary-900 mr-4"
                    >
                      Analytics
                    </a>
                    <button className="text-primary-600 hover:text-primary-900">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
